import type {
  ProfileFieldOption,
  ProfileFieldResponse,
  ProfileRegionResponse,
  ProfileSectionResponse,
} from "@services/facilityManagement";
import {
  ACTIVITIES_SECTION_KEY,
  fieldVisibleForActivityToggles,
  isDeprecatedProfileField,
} from "@utils/facilityProfileActivityDefaults";

/** Date-ish renderers used for Medicare certification date pickers. */
export function isDateProfileField(field: ProfileFieldResponse): boolean {
  const t = (field.hub_renderer_key || field.field_type || "").toLowerCase();
  return t === "date";
}

function _isDateLikeField(field: ProfileFieldResponse): boolean {
  return isDateProfileField(field);
}

/** Max width for compact profile date inputs (matches Medicare & Medicaid date grid). */
export const PROFILE_DATE_FIELD_MAX_WIDTH_PX = 360;

/**
 * True when this field is the Medicare **original / CMS** certification date
 * (maps to ``facilities.medicare_certification_date``), including templates that use a
 * non-canonical ``field_key`` but the same Hub label.
 */
export function isMedicareCertificationDateField(field: ProfileFieldResponse): boolean {
  const lbl0 = (field.label || "").toLowerCase();
  // Lab-anchor presentation re-labels the Medicare cert field — do not offer CMS refresh there.
  if (lbl0.includes("clia /")) return false;

  const k = field.field_key.toLowerCase();
  if (k === "medicare_certification_date") return true;
  if (k.includes("revalidation") || k.includes("medicaid")) return false;
  if (k.includes("medicare") && k.includes("cert") && k.includes("date")) return true;

  const lbl = (field.label || "").toLowerCase();
  if (lbl.includes("clia")) return false;
  if (
    _isDateLikeField(field) &&
    lbl.includes("medicare") &&
    lbl.includes("certification") &&
    !lbl.includes("revalidation")
  ) {
    if (
      lbl.includes("original") ||
      lbl.includes("cms certification") ||
      lbl.includes("medicare certification")
    ) {
      return true;
    }
  }
  return false;
}

/** True for the profile CCN / Medicare provider number field (``field_key`` ``ccn``). */
export function isCcnProfileField(field: ProfileFieldResponse): boolean {
  return field.field_key.trim().toLowerCase() === "ccn";
}

/** First matching Medicare certification date field in the schema, or the canonical key. */
export function resolveMedicareCertificationDateFieldKey(
  sections: ProfileSectionResponse[],
): string {
  for (const s of sections) {
    for (const f of s.fields) {
      if (isMedicareCertificationDateField(f)) return f.field_key;
    }
  }
  return "medicare_certification_date";
}

/** Medicare PECOS / enrollment revalidation date field (excludes Medicaid-only). */
export function isMedicareRevalidationDateField(field: ProfileFieldResponse): boolean {
  const k = field.field_key.toLowerCase();
  if (k.includes("medicaid")) return false;
  if (k === "medicare_revalidation_date") return true;
  if (k.includes("medicare") && k.includes("revalidation")) return true;
  const lbl = (field.label || "").toLowerCase();
  return (
    _isDateLikeField(field) &&
    lbl.includes("medicare") &&
    lbl.includes("revalidation") &&
    !lbl.includes("medicaid")
  );
}

export const CMS_ACCREDITING_BODY_OPTION: ProfileFieldOption = {
  key: "cms",
  label: "Centers for Medicare & Medicaid Services (Federal Certification)",
};

/** True for the accrediting-body picker (API may use `accrediting_bodies` or a select/multiselect on an accredit* key). */
export function isAccreditingProfileField(field: ProfileFieldResponse): boolean {
  const renderer = field.hub_renderer_key || field.field_type;
  return (
    renderer === "accrediting_bodies" ||
    ((field.field_type === "select" || field.field_type === "multiselect") &&
      field.field_key.toLowerCase().includes("accredit"))
  );
}

export function isCmsAccreditingBodyOption(option: ProfileFieldOption): boolean {
  const key = option.key.toLowerCase();
  const label = option.label.toLowerCase();
  return (
    key === "cms" ||
    key.includes("federal") ||
    label.includes("centers for medicare") ||
    label.includes("federal certification")
  );
}

export function ensureCmsAccreditingBodyOption(
  options: ProfileFieldOption[] | null | undefined,
): ProfileFieldOption[] {
  const list = options ?? [];
  if (list.some(isCmsAccreditingBodyOption)) return list;
  return [CMS_ACCREDITING_BODY_OPTION, ...list];
}

/**
 * Initial profile value when no saved accrediting selection exists.
 * `accrediting_bodies` renderer expects `{ slug, name }[]`; multiselect uses slug strings.
 */
export function cmsDefaultAccreditingProfileValue(
  field: ProfileFieldResponse,
  cmsOption: ProfileFieldOption,
): unknown {
  const renderer = field.hub_renderer_key || field.field_type;
  if (renderer === "accrediting_bodies") {
    return [{ slug: cmsOption.key, name: cmsOption.label }];
  }
  return [cmsOption.key];
}

/**
 * Non-empty accreditor keys from a saved profile inner value (`value_json.value`).
 * Null, empty arrays, whitespace-only slugs, and placeholder objects like `{}` count as
 * unset so previously saved facilities without a real choice get the CMS default.
 */
export function accreditingSavedSelectionSlugs(inner: unknown): string[] {
  if (inner === null || inner === undefined) return [];
  if (!Array.isArray(inner)) {
    if (typeof inner === "string") {
      const s = inner.trim();
      return s ? [s] : [];
    }
    if (typeof inner === "object") {
      return accreditingSavedSelectionSlugs([inner]);
    }
    return [];
  }
  const slugs: string[] = [];
  for (const item of inner) {
    if (typeof item === "string") {
      const s = item.trim();
      if (s) slugs.push(s);
      continue;
    }
    if (item && typeof item === "object") {
      const rec = item as Record<string, unknown>;
      const slug = rec.slug;
      if (typeof slug === "string" && slug.trim()) {
        slugs.push(slug.trim());
        continue;
      }
      const key = rec.key;
      if (typeof key === "string" && key.trim()) {
        slugs.push(key.trim());
      }
    }
  }
  return slugs;
}

/**
 * Short phrases for the cert "Identifiers & accreditation" region subtitle, derived from
 * the fields actually shown (avoids static copy that lists CLIA when CLIA lives elsewhere).
 */
export function descriptorForIdentifierField(field: ProfileFieldResponse): string | null {
  const k = field.field_key.toLowerCase();
  const lbl = (field.label || "").toLowerCase();
  const renderer = field.hub_renderer_key || field.field_type;

  if (
    renderer === "accrediting_bodies" ||
    ((field.field_type === "select" || field.field_type === "multiselect") && k.includes("accredit"))
  ) {
    return "accrediting bodies";
  }
  if (k.includes("clia") || lbl.includes("clia")) return "CLIA";
  if (
    k.includes("ccn") ||
    k.includes("cms_certification") ||
    lbl.includes("(ccn)") ||
    lbl.includes(" ccn") ||
    lbl.includes("cms certification number")
  ) {
    return "CCN";
  }
  if (k.includes("npi") || lbl.includes("national provider identifier")) return "NPI";
  const trimmed = (field.label || field.field_key).trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Builds a comma list with "and" before the last item; dedupes while preserving order. */
export function buildCertIdentifiersRegionSubtitle(fields: ProfileFieldResponse[]): string | null {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const f of fields) {
    if (f.visible === false) continue;
    const d = descriptorForIdentifierField(f);
    if (!d) continue;
    const key = d.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(d);
  }
  if (parts.length === 0) return null;

  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/**
 * Region cards where date fields use the compact side-by-side grid (sm+), same as
 * "Medicare & Medicaid dates" on Certification & Enrollment.
 */
export function regionUsesTwoColumnDateGrid(
  region: ProfileRegionResponse,
  fieldsInRegion?: ProfileFieldResponse[],
): boolean {
  const label = (region.label || "").toLowerCase();
  if (label.includes("medicare") && label.includes("medicaid")) return true;
  if (label.includes("inspection") && label.includes("plan")) return true;
  if (fieldsInRegion && fieldsInRegion.length > 0) {
    const visible = fieldsInRegion.filter((f) => f.visible !== false);
    if (visible.length > 0 && visible.every(isDateProfileField)) return true;
  }
  return false;
}

/** True when API sent regions or any non-default per-field layout metadata. */
export function sectionUsesAdvancedLayout(section: ProfileSectionResponse): boolean {
  const regions = section.regions ?? [];
  if (regions.length > 0) return true;
  return section.fields.some((f) => {
    if (f.visible === false) return false;
    if (f.region_key && String(f.region_key).length > 0) return true;
    if ((f.layout_row ?? 0) !== 0) return true;
    if ((f.layout_column_span ?? 1) !== 1) return true;
    if (f.layout_width_pct != null) return true;
    if ((f.layout_order ?? 0) !== 0) return true;
    return false;
  });
}

export function groupFieldsIntoRows(fields: ProfileFieldResponse[]): ProfileFieldResponse[][] {
  const vis = fields.filter((f) => f.visible !== false);
  const byRow = new Map<number, ProfileFieldResponse[]>();
  for (const f of vis) {
    const r = f.layout_row ?? 0;
    if (!byRow.has(r)) byRow.set(r, []);
    byRow.get(r)!.push(f);
  }
  return [...byRow.keys()]
    .sort((a, b) => a - b)
    .map((k) =>
      (byRow.get(k) ?? []).sort(
        (a, b) => (a.layout_order ?? 0) - (b.layout_order ?? 0),
      ),
    );
}

export function regionStyleSx(style?: Record<string, unknown> | null): Record<string, unknown> {
  if (!style || typeof style !== "object") return {};
  const v = style.variant;
  if (v === "bordered") {
    return {
      border: "1px solid #e5e7eb",
      borderRadius: 2,
      p: 2,
      mb: 2,
      backgroundColor: "#fff",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    };
  }
  if (v === "callout_blue") {
    return {
      backgroundColor: "#eff6ff",
      borderRadius: 2,
      p: 2,
      mb: 2,
      border: "1px solid #bfdbfe",
      boxShadow: "0 1px 2px rgba(37, 99, 235, 0.06)",
    };
  }
  return {};
}

/** Profile fields that always stay visible regardless of ``tracked_anchors``. */
export const ANCHOR_CONTROL_FIELD_KEYS: ReadonlySet<string> = new Set([
  "tracked_anchors",
  "is_dmepos",
  "lab_anchor_active",
  // Classification control (COW vs full cert). Not a calendar-anchor date.
  // RHC defaults omit the clia anchor, so gating this key hid #fp-ctrl-clia_certificate_type (AB#10486).
  "clia_certificate_type",
]);

/**
 * Coerce ``tracked_anchors`` multiselect value to string keys.
 * When empty, callers should treat as "show all anchor-date fields" (backward compatible).
 */
export function coerceTrackedAnchors(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === "string" ? x.trim() : String(x ?? "").trim()))
    .filter(Boolean);
}

/**
 * Map a profile ``field_key`` to the anchor key(s) that govern its visibility.
 * Fields not returned here are always shown (not anchor-governed).
 */
export function anchorsForProfileFieldKey(fieldKey: string): string[] {
  const k = fieldKey.toLowerCase();
  const out = new Set<string>();
  if (
    k === "ccn" ||
    k.includes("cms_certification") ||
    k === "medicare_certification_date" ||
    k.includes("facility_unique")
  ) {
    out.add("medicare_cert");
  }
  if (k.includes("medicare_revalidation") || (k.includes("revalidation") && k.includes("medicare"))) {
    out.add("medicare_revalidation");
  }
  if (k.includes("medicaid") && k.includes("revalidation")) {
    out.add("medicare_revalidation");
  }
  if (k.startsWith("state_lab_license")) {
    out.add("clia");
  }
  if (k.startsWith("state_license_renewal_")) {
    out.add("state_license");
  }
  if (
    k.startsWith("state_license") ||
    k.includes("state_facility_license") ||
    k.includes("facility_operating_license") ||
    k.includes("facility_license") ||
    k.startsWith("professional_license") ||
    k.includes("physician_license") ||
    k.includes("medical_license")
  ) {
    out.add("state_license");
  }
  if (k.startsWith("clia")) {
    out.add("clia");
  }
  if (k === "accrediting_body" || k.startsWith("accreditation")) {
    out.add("accreditation");
  }
  if (k.startsWith("pharmacy")) {
    out.add("pharmacy");
  }
  if (k.startsWith("radiation")) {
    out.add("radiation");
  }
  if (k.startsWith("food")) {
    out.add("food");
  }
  if (k.startsWith("con_") || k === "con_approval_date" || k === "con_project_milestone_date") {
    out.add("con");
  }
  if (k.includes("dea")) {
    out.add("dea");
  }
  return [...out];
}

/**
 * When ``tracked_anchors`` is non-empty, hide anchor-specific permit/date fields that
 * are not selected. Control toggles and ``tracked_anchors`` itself stay visible.
 */
export function fieldVisibleForTrackedAnchors(
  field: ProfileFieldResponse,
  trackedRaw: unknown,
): boolean {
  if (field.visible === false) return false;
  if (ANCHOR_CONTROL_FIELD_KEYS.has(field.field_key)) return true;
  const tracked = coerceTrackedAnchors(trackedRaw);
  if (tracked.length === 0) return true;
  const anchors = anchorsForProfileFieldKey(field.field_key);
  if (anchors.length === 0) return true;
  return anchors.some((a) => tracked.includes(a));
}

/** Section key for operational permits / registrations (aggregator + rules profile seed). */
export const OPERATIONAL_LICENSING_SECTION_KEY = "operational_licensing";

function applyActivityAndDeprecatedFilters(
  fields: ProfileFieldResponse[],
  fieldValues: Record<string, unknown>,
): ProfileFieldResponse[] {
  return fields.filter(
    (f) =>
      !isDeprecatedProfileField(f.field_key) &&
      fieldVisibleForActivityToggles(f, fieldValues),
  );
}

/**
 * Apply ``tracked_anchors`` visibility to a section's fields.
 * If the anchor filter alone would hide **every** field, fall back to anchor-visible fields
 * (then activity filters) so wizard steps (e.g. Operational licenses) never render blank
 * when defaults omit optional anchors such as DEA for SNFs.
 * Activity toggles still hide gated fields when the user turned the activity off.
 */
export function filterFieldsForTrackedAnchors(
  fields: ProfileFieldResponse[],
  trackedRaw: unknown,
  fieldValues?: Record<string, unknown>,
): ProfileFieldResponse[] {
  const base = fields.filter((f) => f.visible !== false);
  const afterAnchor = base.filter((f) => fieldVisibleForTrackedAnchors(f, trackedRaw));
  if (!fieldValues) {
    if (afterAnchor.length === 0 && base.length > 0) {
      return base;
    }
    return afterAnchor;
  }
  const afterActivity = applyActivityAndDeprecatedFilters(afterAnchor, fieldValues);
  if (afterActivity.length > 0) {
    return afterActivity;
  }
  if (afterAnchor.length === 0 && base.length > 0) {
    return applyActivityAndDeprecatedFilters(base, fieldValues);
  }
  return afterActivity;
}

/** True when at least one non-CMS accreditor is selected (mixed CMS+AO counts as non-CMS for calendar emphasis). */
export function nonCmsAccreditorSelected(slugs: string[]): boolean {
  if (!slugs.length) return false;
  const allCms = slugs.every((slug) =>
    isCmsAccreditingBodyOption({ key: slug, label: slug }),
  );
  return !allCms;
}

export interface AnchorPresentationContext {
  labAnchorActive: boolean;
  nonCmsAccreditor: boolean;
}

/**
 * Adjust labels/help for Medicare date fields when CLIA anchor or non-CMS AO context applies.
 * Field keys and values are unchanged — this is presentation-only.
 */
export function augmentFieldForAnchorPresentation(
  field: ProfileFieldResponse,
  ctx: AnchorPresentationContext,
): ProfileFieldResponse {
  const certLike = isMedicareCertificationDateField(field);
  const revLike = isMedicareRevalidationDateField(field);
  if (ctx.labAnchorActive && certLike) {
    return {
      ...field,
      label: "CLIA / CMS certification date (Medicare certification from CMS POS)",
      help_text:
        field.help_text ??
        "When CLIA is your primary lab anchor, this date still reflects CMS POS certification data when available.",
    };
  }
  if (ctx.labAnchorActive && revLike) {
    return {
      ...field,
      label: "Enrollment / revalidation reference (PECOS cadence)",
      help_text:
        field.help_text ??
        'Lab calendars prioritize CLIA certificate dates when "Use CLIA certificate as primary lab compliance anchor" is on.',
    };
  }
  if (ctx.nonCmsAccreditor && certLike) {
    return {
      ...field,
      label: "Certification / agreement date (CMS POS or AO)",
      help_text:
        field.help_text ??
        "Non-CMS accreditors: calendar items may follow accreditation expiration — confirm dates with your AO.",
    };
  }
  if (ctx.nonCmsAccreditor && revLike) {
    return {
      ...field,
      label: "Revalidation / accreditation renewal reference",
      help_text:
        field.help_text ??
        "When accredited by an AO, renewal cycles follow the organization — not automatically the Medicare certification date.",
    };
  }
  return field;
}

/** Profile tab order: Activities & Programs first, then API display_order. */
export function compareProfileSectionsForDisplay(
  a: ProfileSectionResponse,
  b: ProfileSectionResponse,
): number {
  const aActivities = a.section_key === ACTIVITIES_SECTION_KEY;
  const bActivities = b.section_key === ACTIVITIES_SECTION_KEY;
  if (aActivities !== bActivities) return aActivities ? -1 : 1;
  return (
    (a.display_order ?? 0) - (b.display_order ?? 0) ||
    a.section_key.localeCompare(b.section_key)
  );
}
